import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Code, Smartphone, Server, Cloud, Users, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import Layout from '../components/layout/Layout';
import { useMockData } from '../data/useMockData';

const serviceIcons = {
  'web-development': Code,
  'mobile-development': Smartphone,
  'backend-apis': Server,
  'cloud-devops': Cloud,
  'consulting-architecture': Users,
};

const ServicesPage = () => {
  const { t } = useTranslation();
  const { services } = useMockData();
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  return (
    <Layout>
      {/* Hero */}
      <section className="pt-32 pb-20 relative">
        <div className="grid-pattern absolute inset-0 opacity-30" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-semibold text-white mb-6">
              {t('services.pageTitle')}
            </h1>
            <p className="text-xl text-white/70 leading-relaxed">
              {t('services.pageDescription')}
            </p>
          </div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="pb-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="space-y-24">
            {services.map((service, index) => {
              const IconComponent = serviceIcons[service.id];
              const isEven = index % 2 === 0;
              
              return (
                <div 
                  key={service.id} 
                  id={service.id}
                  className="scroll-mt-32"
                >
                  <div className={`grid lg:grid-cols-2 gap-12 items-start ${isEven ? '' : 'lg:flex-row-reverse'}`}>
                    {/* Content */}
                    <div className={isEven ? '' : 'lg:order-2'}>
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-14 h-14 bg-[#00FFD1]/10 flex items-center justify-center">
                          {IconComponent && <IconComponent className="w-7 h-7 text-[#00FFD1]" />}
                        </div>
                        <h2 className="text-3xl md:text-4xl font-semibold text-white">
                          {service.title}
                        </h2>
                      </div>
                      
                      <p className="text-lg text-white/70 mb-8 leading-relaxed">
                        {service.description}
                      </p>
                      
                      {/* Technologies */}
                      <div className="mb-8">
                        <h3 className="text-sm text-white/70 uppercase tracking-wider mb-4">
                          {t('services.technologies')}
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {service.technologies.map((tech) => (
                            <span 
                              key={tech} 
                              className="px-4 py-2 bg-white/5 border border-white/10 text-white/80 text-sm font-medium"
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <Link to="/contact">
                        <Button className="bg-[#00FFD1] text-black hover:bg-[rgba(0,255,209,0.1)] hover:text-[#00FFD1] px-6 py-3">
                          {t('services.discussProject')}
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    </div>
                    
                    {/* Cards */}
                    <div className={`space-y-6 ${isEven ? '' : 'lg:order-1'}`}>
                      {/* Typical Missions */}
                      <div className="p-8 bg-white/[0.02] border border-white/10">
                        <h3 className="text-lg font-semibold text-white mb-6">{t('services.typicalMissions')}</h3>
                        <ul className="space-y-4">
                          {service.missions.map((mission, idx) => (
                            <li key={idx} className="flex items-start gap-3">
                              <Check className="w-5 h-5 text-[#00FFD1] mt-0.5 flex-shrink-0" />
                              <span className="text-white/70">{mission}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      {/* Business Outcomes */}
                      <div className="p-8 bg-[#00FFD1]/5 border border-[#00FFD1]/20">
                        <h3 className="text-lg font-semibold text-white mb-6">{t('services.businessOutcomes')}</h3>
                        <ul className="space-y-4">
                          {service.outcomes.map((outcome, idx) => (
                            <li key={idx} className="flex items-start gap-3">
                              <div className="w-2 h-2 bg-[#00FFD1] mt-2 flex-shrink-0" />
                              <span className="text-white/70">{outcome}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                  
                  {/* Divider */}
                  {index < services.length - 1 && (
                    <div className="border-b border-white/10 mt-24" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#0A0A0A] border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl md:text-5xl font-semibold text-white mb-6">
            {t('services.notSure')}
          </h2>
          <p className="text-lg text-white/60 max-w-2xl mx-auto mb-10">
            {t('services.notSureDesc')}
          </p>
          <Link to="/contact">
            <Button className="bg-[#00FFD1] text-black hover:bg-[rgba(0,255,209,0.1)] hover:text-[#00FFD1] px-8 py-4 text-lg font-medium h-auto">
              {t('services.bookConsultation')}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default ServicesPage;
